import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";

/**
 * useDashboardData — single hook that owns every fetch the Dashboard page
 * needs: KPI summary, latest energy / water rows for "Today's Change", and
 * the data-source list for the Home Intelligence card.
 *
 * Returning a flat object lets `Dashboard.jsx` stay a pure layout file.
 *
 * @param {{id: string} | null} site - the active site, or null while loading
 *   the SiteContext. The hook is a no-op when `site` is null so the
 *   component can mount before site discovery completes.
 */
export default function useDashboardData(site) {
  const [data, setData] = useState(null);
  const [latestEnergy, setLatestEnergy] = useState(null);
  const [latestWater, setLatestWater] = useState(null);
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const reload = useCallback(async () => {
    if (!site) return;
    setLoading(true);
    setErr(null);
    try {
      const [dashboardRes, energyRes, waterRes, sourcesRes] = await Promise.all([
        api.get(`/sites/${site.id}/dashboard?days=30`),
        api.get(`/sites/${site.id}/energy/daily?days=1`),
        api.get(`/sites/${site.id}/water/daily?days=1`),
        api.get(`/sites/${site.id}/data-sources`),
      ]);
      setData(dashboardRes.data);
      // Backend returns rows ascending by date; the last element is "today".
      // `.at(-1)` is null-safe and avoids index-out-of-range when empty.
      setLatestEnergy(energyRes.data?.rows?.at(-1) || null);
      setLatestWater(waterRes.data?.rows?.at(-1) || null);
      setSources(sourcesRes.data || []);
    } catch (e) {
      setErr(e.userMessage || "Could not load dashboard");
    } finally {
      setLoading(false);
    }
  }, [site]);

  useEffect(() => {
    reload();
  }, [reload]);

  return { data, latestEnergy, latestWater, sources, loading, err, reload };
}
